import React, { useState } from 'react';
import {
  Table, Card, Button, Tag, Space, Typography, Modal,
  Descriptions, Row, Col, Tooltip, Divider
} from 'antd';
import {
  EyeOutlined, DownloadOutlined, CloudUploadOutlined,
  DeleteOutlined, CheckCircleOutlined, InfoCircleOutlined
} from '@ant-design/icons';
import axios from 'axios';
import logger from '../../services/logger';
import { safeFormatTimestamp } from '../../utils/timezone';

const { Text } = Typography;

const formatFileSize = (size) => {
  if (!size) return '-';
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / 1024 / 1024).toFixed(2)} MB`;
};

const FirmwareList = ({ firmwares, loading, onDeploy, onDelete, onRefresh, showActions = true }) => {
  const [selectedFirmware, setSelectedFirmware] = useState(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);

  const handleDownload = async (firmware) => {
    try {
      const response = await axios.get(`/api/v1/firmware/download/${firmware.id}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `firmware_${firmware.version}.bin`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      logger.error('Download error:', error);
    }
  };

  const columns = [
    {
      title: 'Versiyon',
      dataIndex: 'version',
      key: 'version',
      width: 140,
      sorter: (a, b) => {
        const vA = a.version.replace('v', '').split('.').map(Number);
        const vB = b.version.replace('v', '').split('.').map(Number);
        return vA.join('') - vB.join('');
      },
      render: (version, record) => (
        <Space>
          <Tag color="blue">{version}</Tag>
          {record.is_critical && <Tag color="red">Kritik</Tag>}
        </Space>
      ),
    },
    {
      title: 'Açıklama',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (d) => d || <Text type="secondary">-</Text>,
    },
    {
      title: 'Boyut',
      dataIndex: 'file_size',
      key: 'file_size',
      width: 100,
      sorter: (a, b) => (a.file_size || 0) - (b.file_size || 0),
      render: (size) => formatFileSize(size),
    },
    {
      title: 'Durum',
      key: 'status',
      width: 80,
      render: (_, record) => (
        <Tag color={record.is_active ? 'green' : 'red'}>
          {record.is_active ? 'Aktif' : 'Pasif'}
        </Tag>
      ),
    },
    {
      title: 'Tarih',
      dataIndex: 'release_date',
      key: 'release_date',
      width: 160,
      sorter: (a, b) => new Date(a.release_date || 0) - new Date(b.release_date || 0),
      render: (date) => date ? safeFormatTimestamp(date) : '-',
    },
    ...(showActions ? [{
      title: 'İşlemler',
      key: 'actions',
      width: 180,
      render: (_, record) => (
        <Space size={4}>
          <Tooltip title="Detay">
            <Button icon={<EyeOutlined />} onClick={() => { setSelectedFirmware(record); setDetailModalVisible(true); }} size="small" type="text" />
          </Tooltip>
          <Tooltip title="Dağıt">
            <Button icon={<CloudUploadOutlined />} type="primary" onClick={() => onDeploy(record.id)} size="small">Dağıt</Button>
          </Tooltip>
          <Tooltip title="Sil">
            <Button icon={<DeleteOutlined />} danger onClick={() => onDelete(record.id)} size="small" type="text" />
          </Tooltip>
        </Space>
      ),
    }] : []),
  ];

  return (
    <div>
      <Table
        columns={columns}
        dataSource={firmwares}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 20, showSizeChanger: true, showTotal: (total) => `Toplam ${total} firmware` }}
        size="small"
      />

      {/* Firmware Detay Modal */}
      <Modal
        title="Firmware Detayı"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={null}
        width={700}
      >
        {selectedFirmware && (
          <div>
            <Descriptions bordered column={2} size="small">
              <Descriptions.Item label="Versiyon">
                <Tag color="blue">{selectedFirmware.version}</Tag>
                {selectedFirmware.is_critical && <Tag color="red" style={{ marginLeft: 4 }}>Kritik</Tag>}
              </Descriptions.Item>
              <Descriptions.Item label="Boyut">{formatFileSize(selectedFirmware.file_size)}</Descriptions.Item>
              <Descriptions.Item label="Durum">
                <Tag color={selectedFirmware.is_active ? 'green' : 'red'}>
                  {selectedFirmware.is_active ? 'Aktif' : 'Pasif'}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Yayın Tarihi">{selectedFirmware.release_date ? safeFormatTimestamp(selectedFirmware.release_date) : '-'}</Descriptions.Item>
              <Descriptions.Item label="Açıklama" span={2}>{selectedFirmware.description || '-'}</Descriptions.Item>
              <Descriptions.Item label="Release Notları" span={2}>{selectedFirmware.release_notes || '-'}</Descriptions.Item>
              <Descriptions.Item label="SHA-256" span={2}>
                <code style={{ fontSize: 11, wordBreak: 'break-all' }}>{selectedFirmware.sha256_checksum || '-'}</code>
              </Descriptions.Item>
            </Descriptions>

            <Divider />

            <Row gutter={16}>
              <Col span={12}>
                <Button type="primary" icon={<CloudUploadOutlined />} onClick={() => { setDetailModalVisible(false); onDeploy(selectedFirmware.id); }} block>Cihaza Dağıt</Button>
              </Col>
              <Col span={12}>
                <Button icon={<DownloadOutlined />} onClick={() => handleDownload(selectedFirmware)} block>İndir</Button>
              </Col>
            </Row>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default FirmwareList;
